import React, { useState, useEffect } from 'react';
import { Users, Calendar, CheckSquare, Clock, TrendingUp, Award } from 'lucide-react';
import { BarChart, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

const Dashboard = ({ employees, attendance, tasks }) => {
  const [animatedValues, setAnimatedValues] = useState({});
  
  // Calculate statistics
  const today = new Date().toISOString().split('T')[0];
  const todayAttendance = attendance.filter(a => a.date === today);
  const presentToday = todayAttendance.filter(a => a.status === 'present').length;
  const absentToday = todayAttendance.filter(a => a.status === 'absent').length;
  const halfDayToday = todayAttendance.filter(a => a.status === 'half-day').length;

  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    done: tasks.filter(t => t.status === 'done').length
  };

  // Faster animation on load - reduced duration for quicker display
  useEffect(() => {
    const stats = {
      employees: employees.length,
      present: presentToday,
      tasks: taskStats.total,
      pending: taskStats.pending
    };

    // Immediately set final values for instant display
    setAnimatedValues(stats);
    
    // Optional: Add subtle count-up animation with much faster duration
    Object.keys(stats).forEach(key => {
      let start = 0;
      const end = stats[key];
      const duration = 300; // Reduced from 1000ms to 300ms for faster animation
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          start = end;
          clearInterval(timer);
        }
        setAnimatedValues(prev => ({ ...prev, [key]: Math.floor(start) }));
      }, 16);
    });
  }, [employees.length, presentToday, taskStats.total, taskStats.pending]);

  // Chart data with enhanced colors
  const attendanceData = [
    { name: 'Present', value: presentToday, color: '#10b981' },
    { name: 'Absent', value: absentToday, color: '#ef4444' },
    { name: 'Half Day', value: halfDayToday, color: '#f59e0b' }
  ];

  const taskData = [
    { name: 'Pending', value: taskStats.pending, color: '#6b7280' },
    { name: 'In Progress', value: taskStats.inProgress, color: '#3b82f6' },
    { name: 'Done', value: taskStats.done, color: '#10b981' }
  ];

  const StatCard = ({ title, value, icon: Icon, color, subtitle, gradient, delay = 0 }) => (
    <div 
      className="group bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl p-6 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 animate-slide-up border border-white/20"
      style={{ animationDelay: '0ms' }} // Removed staggered delay for simultaneous loading
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color} transition-all duration-300`}>
            {animatedValues[title.toLowerCase().replace(' ', '')] || value}
          </p>
          {subtitle && <p className="text-sm text-gray-500 mt-2">{subtitle}</p>}
        </div>
        <div className={`p-4 rounded-2xl ${gradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
      </div>
      <div className="mt-4 flex items-center text-sm text-green-600">
        <TrendingUp className="w-4 h-4 mr-1" />
        <span>+12% from last month</span>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, delay = 0 }) => (
    <div 
      className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl p-6 transition-all duration-300 transform hover:scale-[1.02] animate-slide-up border border-white/20"
      style={{ animationDelay: '0ms' }} // Load immediately
    >
      <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-6">{title}</h3>
      {children}
    </div>
  );

  const RecentActivity = () => (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl p-6 transition-all duration-300 animate-slide-up border border-white/20" style={{ animationDelay: '0ms' }}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">Recent Activity</h3>
        <Award className="w-5 h-5 text-primary-500" />
      </div>
      <div className="space-y-3">
        {tasks.slice(0, 5).map((task, index) => {
          const employee = employees.find(e => e.id === task.employeeId);
          const statusColor = {
            'pending': 'text-gray-600 bg-gray-100 border-gray-200',
            'in-progress': 'text-blue-600 bg-blue-100 border-blue-200',
            'done': 'text-green-600 bg-green-100 border-green-200'
          }[task.status];

          return (
            <div 
              key={task.id} 
              className="flex items-center space-x-4 p-4 hover:bg-white/60 rounded-xl transition-all duration-200 transform hover:scale-[1.02] border border-transparent hover:border-white/40"
            >
              <div className="relative">
                <img
                  src={employee?.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(employee?.name || 'User')}&background=random&size=40`}
                  alt={employee?.name}
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-md"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate">{task.title}</p>
                <p className="text-sm text-gray-500">{employee?.name}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColor} transition-all duration-200 hover:scale-105`}>
                {task.status.replace('-', ' ')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Header */}
      <div className="text-center py-8 animate-slide-down">
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-primary-800 bg-clip-text text-transparent mb-4">
          Welcome Back! 👋
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Here's what's happening with your team today. Monitor attendance, track tasks, and stay on top of your workforce management.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard
          title="Total Employees"
          value={employees.length}
          icon={Users}
          color="text-primary-600"
          gradient="bg-gradient-to-r from-primary-500 to-primary-600"
        />
        <StatCard
          title="Present Today"
          value={presentToday}
          icon={CheckSquare}
          color="text-green-600"
          subtitle={`${employees.length - todayAttendance.length} not marked`}
          gradient="bg-gradient-to-r from-green-500 to-emerald-600"
        />
        <StatCard
          title="Total Tasks"
          value={taskStats.total}
          icon={CheckSquare}
          color="text-purple-600"
          subtitle={`${taskStats.done} completed`}
          gradient="bg-gradient-to-r from-purple-500 to-indigo-600"
        />
        <StatCard
          title="Pending Tasks"
          value={taskStats.pending}
          icon={Clock}
          color="text-orange-600"
          gradient="bg-gradient-to-r from-orange-500 to-red-500"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        <ChartCard title="Today's Attendance Overview">
          {attendanceData.some(d => d.value > 0) ? (
            <div className="relative">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={attendanceData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={40}
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={600}
                  >
                    {attendanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      borderRadius: '12px', 
                      border: 'none',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">{todayAttendance.length}</div>
                  <div className="text-sm text-gray-500">Total</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Calendar className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">No attendance data for today</p>
              <p className="text-sm">Start marking attendance to see insights</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            {attendanceData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full shadow-sm"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm font-medium text-gray-700">{item.name}</span>
                </div>
                <span className="text-lg font-bold" style={{ color: item.color }}>{item.value}</span>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Task Progress Analytics">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={taskData}
                cx="50%"
                cy="50%"
                outerRadius={90}
                innerRadius={40}
                dataKey="value"
                animationBegin={0}
                animationDuration={600}
              >
                {taskData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  borderRadius: '12px', 
                  border: 'none',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }} 
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            {taskData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full shadow-sm"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm font-medium text-gray-700">{item.name}</span>
                </div>
                <span className="text-lg font-bold" style={{ color: item.color }}>{item.value}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Recent Activity */}
      <RecentActivity />
    </div>
  );
};

export default Dashboard;